import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import { useAuth } from "@context/auth";
import { isTerminatedUser } from "@utils/cardActions";
import { openToast as showToast } from "@utils/toastStore";
import { ServicesURL, downloadBlobFile, getQueryString } from "@utils/utilsApi";
import { handleSwitchToLearner } from "@utils/utilsCognito";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import EnrollLOModal, { EnrollSubjectType } from "../components/EnrollLOModal";
import AddUsersToGroupModal from "../components/AddUsersToGroupModal";
import GroupEnrollmentStatusModal from "../components/GroupEnrollmentStatusModal";
import RefreshPerimeterDialog, {
  RefreshPerimeterTarget,
} from "../components/RefreshPerimeterDialog";
import { UserItemHandlers } from "../components/UserItem";
import { GroupItemHandlers } from "../components/GroupItem";
import { LO_SECTION_ANCHOR } from "../types";

export type UGGroupKind = "STATIC" | "DYNAMIC";

export interface UGUserActions extends UserItemHandlers {
  onOpenDetail: (user: any) => void;
}

export interface UGGroupActions extends GroupItemHandlers {
  onOpenDetail: (group: any) => void;
}

interface UseUsersGroupsActionsOptions {
  corporateId: string;
  onRefresh?: () => void;
  onClearSelection?: () => void;
}

export interface UseUsersGroupsActionsResult {
  userActions: UGUserActions;
  getGroupActions: (kind: UGGroupKind) => UGGroupActions;
  openEnrollUser: (user: any) => void;
  openEnrollGroup: (group: any, kind: UGGroupKind) => void;
  openWelcomeMail: (user?: any) => void;
  openBlockUser: (user: any) => void;
  openBulkStatus: (
    target: "ACTIVE" | "SUSPENDED",
    ids: (string | number)[],
  ) => void;
  openDeleteUser: (user: any) => void;
  openDeleteGroup: (group: any, kind: UGGroupKind) => void;
  openAddUsersToGroup: (group: any) => void;
  openEnrollmentStatus: (group: any) => void;
  openRefreshPerimeter: (target: RefreshPerimeterTarget) => void;
  modals: React.ReactElement;
}

export const useUsersGroupsActions = ({
  corporateId,
  onRefresh,
  onClearSelection,
}: UseUsersGroupsActionsOptions): UseUsersGroupsActionsResult => {
  const { t } = useTranslation("users-groups-v2");
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const [isConfirming, setIsConfirming] = useState(false);
  const [blockUser, setBlockUser] = useState<any>();
  const [reactivateUser, setReactivateUser] = useState<any>();
  const [bulkStatus, setBulkStatus] = useState<{
    target: "ACTIVE" | "SUSPENDED";
    ids: (string | number)[];
  }>();
  const [deleteUser, setDeleteUser] = useState<any>();
  const [welcomeMailUser, setWelcomeMailUser] = useState<any>();
  const [loginAsUser, setLoginAsUser] = useState<any>();
  const [deleteGroup, setDeleteGroup] = useState<any>();
  const [addUsersGroup, setAddUsersGroup] = useState<any>();
  const [enrollmentStatusGroup, setEnrollmentStatusGroup] = useState<any>();
  const [refreshPerimeterTarget, setRefreshPerimeterTarget] =
    useState<RefreshPerimeterTarget | null>(null);
  const [enrollTarget, setEnrollTarget] = useState<{
    subjectType: EnrollSubjectType;
    subject: any;
    groupType?: UGGroupKind;
  }>();

  const refresh = useCallback(() => {
    onRefresh && onRefresh();
  }, [onRefresh]);

  const notifySuccess = useCallback(
    () =>
      showToast({
        severity: "success",
        message: String(t("confirm-dialog.success")),
      }),
    [t],
  );

  const notifyError = useCallback(
    () =>
      showToast({
        severity: "error",
        message: String(t("confirm-dialog.error")),
      }),
    [t],
  );

  const groupSegment = (kind: UGGroupKind) =>
    kind === "STATIC" ? "groups" : "dynamic-groups";

  const openEnrollUser = useCallback(
    (user: any) => setEnrollTarget({ subjectType: "user", subject: user }),
    [],
  );

  const openEnrollGroup = useCallback(
    (group: any, kind: UGGroupKind) =>
      setEnrollTarget({
        subjectType: "group",
        subject: group,
        groupType: kind,
      }),
    [],
  );

  const openWelcomeMail = useCallback(
    (user: any = {}) => setWelcomeMailUser(user),
    [],
  );

  const openBulkStatus = useCallback(
    (target: "ACTIVE" | "SUSPENDED", ids: (string | number)[]) =>
      setBulkStatus({ target, ids }),
    [],
  );

  const openDeleteGroup = useCallback(
    (group: any, kind: UGGroupKind) =>
      setDeleteGroup({ ...group, groupType: kind }),
    [],
  );

  const onDownloadCsv = async (u: any) => {
    if (!u?.id_user) return;
    try {
      await downloadBlobFile(
        `${ServicesURL.cvFormative}?studentId=${u.id_user}`,
        null,
        `cv_formativo_${u.id_user}.csv`,
        MethodType.GET,
      );
    } catch (error) {
      console.error(error);
      showToast({
        severity: "error",
        message: String(t("download-cv-error")),
      });
    }
  };

  const userActions: UGUserActions = {
    onEnroll: openEnrollUser,
    onDownloadCsv,
    onViewLO: (user) =>
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/${user.id_user}#${LO_SECTION_ANCHOR}`,
      ),
    onImpersonate: (user) => setLoginAsUser(user),
    onEdit: (user) =>
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/${user.id_user}/edit`,
      ),
    onBlock: (user) => setBlockUser(user),
    onReactivate: (user) => setReactivateUser(user),
    onDelete: (user) => setDeleteUser(user),
    onOpenDetail: (user) =>
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/${user.id_user}`,
      ),
  };

  const getGroupActions = (kind: UGGroupKind): UGGroupActions => ({
    onEnroll: (group) => openEnrollGroup(group, kind),
    onViewLO: (group) =>
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/${groupSegment(
          kind,
        )}/${group.id}#${LO_SECTION_ANCHOR}`,
      ),
    onEdit: (group) =>
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/${groupSegment(
          kind,
        )}/${group.id}/edit`,
      ),
    onDelete: (group) => openDeleteGroup(group, kind),
    onEnrollmentStatus: (group) => setEnrollmentStatusGroup(group),
    onRefreshPerimeter:
      kind === "DYNAMIC"
        ? (group) => setRefreshPerimeterTarget({ scope: "group", group })
        : undefined,
    onOpenDetail: (group) =>
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/${groupSegment(
          kind,
        )}/${group.id}`,
      ),
  });

  const confirmBlockUser = async () => {
    if (!blockUser || isTerminatedUser(blockUser)) return;
    const newStatus = blockUser.status === "active" ? "SUSPENDED" : "ACTIVE";
    setIsConfirming(true);
    try {
      await fetcher(
        getQueryString(`${ServicesURL.putCustomerStatus}/${newStatus}`, {
          users_id: String(blockUser.id_user),
          corporate_id: corporateId,
        }),
        MethodType.PUT,
      );
      refresh();
      setBlockUser(undefined);
      notifySuccess();
    } catch (error) {
      console.error(error);
      notifyError();
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmReactivateUser = async () => {
    if (!reactivateUser) return;
    setIsConfirming(true);
    try {
      await fetcher(
        getQueryString(`${ServicesURL.putCustomerStatus}/ACTIVE`, {
          users_id: String(reactivateUser.id_user),
          corporate_id: corporateId,
        }),
        MethodType.PUT,
      );
      refresh();
      setReactivateUser(undefined);
      notifySuccess();
    } catch (error) {
      console.error(error);
      notifyError();
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmBulkStatus = async () => {
    if (!bulkStatus) return;
    setIsConfirming(true);
    try {
      await Promise.all(
        bulkStatus.ids.map((uid) =>
          fetcher(
            getQueryString(
              `${ServicesURL.putCustomerStatus}/${bulkStatus.target}`,
              { users_id: String(uid), corporate_id: corporateId },
            ),
            MethodType.PUT,
          ),
        ),
      );
      refresh();
      onClearSelection && onClearSelection();
      setBulkStatus(undefined);
      notifySuccess();
    } catch (error) {
      console.error(error);
      notifyError();
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteUser) return;
    setIsConfirming(true);
    try {
      await fetcher(
        getQueryString(`${ServicesURL.deleteCustomer}`, {
          users_id: String(deleteUser.id_user),
          corporate_id: corporateId,
        }),
        MethodType.DELETE,
      );
      refresh();
      setDeleteUser(undefined);
      notifySuccess();
    } catch (error) {
      console.error(error);
      notifyError();
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmWelcomeMail = async () => {
    setIsConfirming(true);
    try {
      await fetcher(
        `${ServicesURL.postWelcomemail}?corporate_id=${corporateId}`,
        MethodType.POST,
      );
      setWelcomeMailUser(undefined);
      notifySuccess();
    } catch (error) {
      console.error(error);
      notifyError();
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmLoginAs = async () => {
    if (!loginAsUser) return;
    setIsConfirming(true);
    try {
      authUser &&
        (await handleSwitchToLearner(authUser, {
          corporateId: loginAsUser.corporate_id,
          externalId: loginAsUser.external_id,
        }));
    } catch (error) {
      console.error(error);
      notifyError();
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmDeleteGroup = async () => {
    if (!deleteGroup) return;
    setIsConfirming(true);
    const url =
      deleteGroup.groupType === "DYNAMIC"
        ? `${ServicesURL.dynamicGroups.replace(
            ":idCorporate",
            corporateId,
          )}/${deleteGroup.id}`
        : `${ServicesURL.staticGroups}/${deleteGroup.id}?id_corporate=${corporateId}`;
    try {
      await fetcher(url, MethodType.DELETE);
      refresh();
      setDeleteGroup(undefined);
      notifySuccess();
    } catch (error) {
      console.error(error);
      notifyError();
    } finally {
      setIsConfirming(false);
    }
  };

  const modals = (
    <>
      <EnrollLOModal
        open={!!enrollTarget}
        onClose={() => setEnrollTarget(undefined)}
        subjectType={enrollTarget?.subjectType ?? "user"}
        subject={enrollTarget?.subject}
        groupType={enrollTarget?.groupType}
        corporateId={corporateId}
        onSuccess={refresh}
      />

      <AddUsersToGroupModal
        open={!!addUsersGroup}
        onClose={() => setAddUsersGroup(undefined)}
        corporateId={corporateId}
        group={addUsersGroup}
        onSuccess={refresh}
      />

      <GroupEnrollmentStatusModal
        open={!!enrollmentStatusGroup}
        onClose={() => setEnrollmentStatusGroup(undefined)}
        corporateId={corporateId}
        group={enrollmentStatusGroup}
      />

      <RefreshPerimeterDialog
        target={refreshPerimeterTarget}
        corporateId={corporateId}
        onClose={() => setRefreshPerimeterTarget(null)}
        onSuccess={refresh}
      />

      <ConfirmDialogCst
        open={!!blockUser}
        title={
          blockUser?.status === "active"
            ? String(t("block-user.title", { ns: "modal" }))
            : String(t("unblock-user.title", { ns: "modal" }))
        }
        description={
          blockUser?.status === "active"
            ? t("block-user.description", { ns: "modal" })
            : t("unblock-user.description", { ns: "modal" })
        }
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        isSubmitting={isConfirming}
        onClose={() => setBlockUser(undefined)}
        onConfirm={confirmBlockUser}
      />

      <ConfirmDialogCst
        open={!!reactivateUser}
        title={String(t("activate-user.title", { ns: "modal" }))}
        description={t("activate-user.description", { ns: "modal" })}
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        isSubmitting={isConfirming}
        onClose={() => setReactivateUser(undefined)}
        onConfirm={confirmReactivateUser}
      />

      <ConfirmDialogCst
        open={!!bulkStatus}
        title={
          bulkStatus?.target === "SUSPENDED"
            ? String(t("block-user.title", { ns: "modal" }))
            : String(t("unblock-user.title", { ns: "modal" }))
        }
        description={
          bulkStatus?.target === "SUSPENDED"
            ? t("block-user.description", { ns: "modal" })
            : t("unblock-user.description", { ns: "modal" })
        }
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        isSubmitting={isConfirming}
        onClose={() => setBulkStatus(undefined)}
        onConfirm={confirmBulkStatus}
      />

      <ConfirmDialogCst
        open={!!deleteUser}
        title={String(t("delete-item", { ns: "modal" }))}
        description={t("confirm-delete", { ns: "modal" })}
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        confirmVariant="danger"
        isSubmitting={isConfirming}
        onClose={() => setDeleteUser(undefined)}
        onConfirm={confirmDeleteUser}
      />

      <ConfirmDialogCst
        open={!!welcomeMailUser}
        title={String(t("confirm-welcome-mail.title", { ns: "modal" }))}
        description={t("confirm-welcome-mail.description", { ns: "modal" })}
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        isSubmitting={isConfirming}
        onClose={() => setWelcomeMailUser(undefined)}
        onConfirm={confirmWelcomeMail}
      />

      <ConfirmDialogCst
        open={!!loginAsUser}
        title={String(t("login-as-title", { ns: "modal" }))}
        description={t("login-as-description", { ns: "modal" })}
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        isSubmitting={isConfirming}
        onClose={() => setLoginAsUser(undefined)}
        onConfirm={confirmLoginAs}
      />

      <ConfirmDialogCst
        open={!!deleteGroup}
        title={String(t("delete-item", { ns: "modal" }))}
        description={t("confirm-delete", { ns: "modal" })}
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        confirmVariant="danger"
        isSubmitting={isConfirming}
        onClose={() => setDeleteGroup(undefined)}
        onConfirm={confirmDeleteGroup}
      />
    </>
  );

  return {
    userActions,
    getGroupActions,
    openEnrollUser,
    openEnrollGroup,
    openWelcomeMail,
    openBlockUser: setBlockUser,
    openBulkStatus,
    openDeleteUser: setDeleteUser,
    openDeleteGroup,
    openAddUsersToGroup: setAddUsersGroup,
    openEnrollmentStatus: setEnrollmentStatusGroup,
    openRefreshPerimeter: setRefreshPerimeterTarget,
    modals,
  };
};
